import {useEffect, useState, type FocusEvent, type FormEvent, type ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {HelpIcon} from '@/components/help-icon'
import {Button} from '@/components/ui/button'
import {Checkbox} from '@/components/ui/checkbox'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Textarea} from '@/components/ui/textarea'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {
  CONSTRUCTOR_CTA_CODES,
  useConstructor,
  useConstruir,
  type Constructor,
  type ConstruirPayload,
} from '@/hooks/sgsst/constructores/use-constructores'
import {translateApiError} from '@/lib/errors'
import {validateRequired} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'

// 3.1.7 usa la genérica «Fecha»: es un programa con varias actividades, no una sesión ni una entrega.
const FECHA_LABEL_KEY: Record<string, string> = {'2.6.1': 'sesion', '4.2.6': 'entrega'}

/** Deja el ítem «Sugerido»: el humano confirma por el flujo normal (constructores-estandar-spec §0). */
export function ConstructorBlock({businessId, code, canEdit}: {businessId: string; code: string; canEdit: boolean}) {
  const {t} = useTranslation()
  const {can_upload_evidence} = useCapabilities(businessId)
  const [open, setOpen] = useState(false)
  // Misma puerta de pago que el panel de soportes.
  if (!CONSTRUCTOR_CTA_CODES.has(code) || !canEdit || !can_upload_evidence) return null
  return (
    <>
      <Button size="sm" variant="outline" data-testid={`constructor-cta-${code}`} onClick={() => setOpen(true)}>
        {t('constructores.cta')}
      </Button>
      {open && <ConstructorDialog businessId={businessId} code={code} onClose={() => setOpen(false)} />}
    </>
  )
}

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

function ConstructorDialog({businessId, code, onClose}: {businessId: string; code: string; onClose: () => void}) {
  const {t} = useTranslation()
  const {data} = useConstructor(businessId, code, true)
  const construir = useConstruir(businessId, code)
  const [fecha, setFecha] = useState(todayIso())
  const [notas, setNotas] = useState('')
  const [comentarios, setComentarios] = useState<Record<string, string>>({})
  const [epp, setEpp] = useState<Record<string, string>>({})
  const [incluidos, setIncluidos] = useState<Record<string, boolean>>({})
  const [actividades, setActividades] = useState<
    Record<string, {fecha_programada: string; responsable: string; observaciones: string}>
  >({})
  const [error, setError] = useState<string | null>(null)
  const [fechaError, setFechaError] = useState<string | undefined>()

  function blurFecha(e: FocusEvent<HTMLInputElement>) {
    setFechaError(validateRequired(e.target.value) ?? undefined)
  }

  // Siembra el pre-llenado una sola vez; después manda lo que el dueño teclee.
  useEffect(() => {
    if (!data?.epp) return
    setEpp((prev) =>
      Object.keys(prev).length > 0
        ? prev
        : Object.fromEntries(data.epp!.map((e) => [e.employee_id, e.epp_sugerido.join(', ')]))
    )
    setIncluidos((prev) =>
      Object.keys(prev).length > 0 ? prev : Object.fromEntries(data.epp!.map((e) => [e.employee_id, true]))
    )
  }, [data])

  useEffect(() => {
    if (!data?.estilos_vida) return
    setActividades((prev) =>
      Object.keys(prev).length > 0
        ? prev
        : Object.fromEntries(
            data.estilos_vida!.actividades_obligatorias.map((titulo) => [
              titulo,
              {fecha_programada: '', responsable: '', observaciones: ''},
            ])
          )
    )
  }, [data])

  function submit(e: FormEvent) {
    e.preventDefault()
    if (!data) return
    const fechaErr = validateRequired(fecha)
    setFechaError(fechaErr ?? undefined)
    if (fechaErr) return
    setError(null)
    const payload: ConstruirPayload = {fecha, notas: notas.trim() || null}
    if (code === '2.6.1') {
      payload.comentarios = Object.entries(comentarios)
        .filter(([, comentario]) => comentario.trim())
        .map(([nombre, comentario]) => ({nombre, comentario: comentario.trim()}))
    }
    if (code === '4.2.6') {
      const entregas = (data.epp ?? [])
        .filter((persona) => incluidos[persona.employee_id])
        .map((persona) => ({
          employee_id: persona.employee_id,
          epp: (epp[persona.employee_id] ?? '')
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean),
        }))
        .filter((entrega) => entrega.epp.length > 0)
      if (entregas.length === 0) {
        setError(t('constructores.epp.needOne'))
        return
      }
      payload.entregas = entregas
    }
    if (code === '3.1.7') {
      const lista = Object.entries(actividades).map(([titulo, campos]) => ({
        titulo,
        fecha_programada: campos.fecha_programada || null,
        responsable: campos.responsable.trim() || null,
        observaciones: campos.observaciones.trim() || null,
      }))
      if (lista.length === 0) {
        setError(t('constructores.estilosVida.needOne'))
        return
      }
      payload.actividades = lista
    }
    construir.mutate(payload, {
      onSuccess: onClose,
      onError: (err) => setError(translateApiError(err)),
    })
  }

  return (
    <Dialog open onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl" data-testid="constructor-dialog">
        <DialogHeader>
          <DialogTitle>{data?.titulo ?? t('constructores.cta')}</DialogTitle>
        </DialogHeader>
        {data && (
          <form onSubmit={submit} className="space-y-4" noValidate>
            <p className="text-sm text-muted-foreground">{data.descripcion}</p>
            {data.rendicion && (
              <RendicionPrefillSection
                data={data}
                comentarios={comentarios}
                onComentario={(nombre, valor) => setComentarios((prev) => ({...prev, [nombre]: valor}))}
              />
            )}
            {data.epp && (
              <EppPrefillSection
                data={data}
                epp={epp}
                incluidos={incluidos}
                onEpp={(id, valor) => setEpp((prev) => ({...prev, [id]: valor}))}
                onIncluido={(id, valor) => setIncluidos((prev) => ({...prev, [id]: valor}))}
              />
            )}
            {data.estilos_vida && (
              <EstilosVidaPrefillSection
                data={data}
                actividades={actividades}
                onCampo={(titulo, campo, valor) =>
                  setActividades((prev) => ({...prev, [titulo]: {...prev[titulo], [campo]: valor}}))
                }
              />
            )}
            <div className="space-y-1.5">
              <Label htmlFor="constructor-fecha">
                {t(`constructores.fecha.${FECHA_LABEL_KEY[code] ?? 'construccion'}`)}
              </Label>
              <Input
                id="constructor-fecha"
                type="date"
                required
                value={fecha}
                onChange={(e) => {
                  setFecha(e.target.value)
                  setFechaError(undefined)
                }}
                onBlur={blurFecha}
                aria-invalid={!!fechaError}
                data-testid="constructor-fecha"
              />
              <FieldError error={fechaError} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="constructor-notas">{t('constructores.notas')}</Label>
              <Textarea
                maxLength={tope('NOTA')}
                id="constructor-notas"
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
                placeholder={t('constructores.notasPlaceholder')}
                data-testid="constructor-notas"
              />
            </div>
            <FormDialogFooter
              error={error}
              pending={construir.isPending}
              submitLabel={t('constructores.build')}
              onCancel={onClose}
            />
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}

function PrefillHeading({children}: {children: ReactNode}) {
  return (
    <p className="flex items-center gap-1 font-mono text-[10px] tracking-wider text-muted-foreground uppercase">
      {children}
    </p>
  )
}

function RendicionPrefillSection({
  data,
  comentarios,
  onComentario,
}: {
  data: Constructor
  comentarios: Record<string, string>
  onComentario: (nombre: string, valor: string) => void
}) {
  const {t} = useTranslation()
  const prefill = data.rendicion!
  return (
    <div className="space-y-4" data-testid="rendicion-prefill">
      <div className="rounded-lg bg-secondary/50 p-3 text-sm">
        {t('constructores.rendicion.resumen', {
          year: prefill.period_year,
          score: prefill.score_pct,
          banda: prefill.banda,
        })}
      </div>
      <div className="space-y-2">
        <PrefillHeading>{t('constructores.rendicion.responsables')}</PrefillHeading>
        {prefill.responsables.length === 0 && (
          <p className="text-xs text-muted-foreground">{t('constructores.rendicion.sinResponsables')}</p>
        )}
        {prefill.responsables.map((responsable) => (
          <div key={responsable.designacion_id} className="space-y-1">
            <p className="text-sm">
              {responsable.nombre} <span className="text-xs text-muted-foreground">· {responsable.designacion}</span>
            </p>
            <Textarea
              maxLength={tope('NOTA')}
              value={comentarios[responsable.nombre] ?? ''}
              onChange={(e) => onComentario(responsable.nombre, e.target.value)}
              placeholder={t('constructores.rendicion.comentarioPlaceholder')}
              data-testid={`comentario-${responsable.designacion_id}`}
            />
          </div>
        ))}
      </div>
      <div className="space-y-1">
        <PrefillHeading>{t('constructores.rendicion.acciones')}</PrefillHeading>
        {prefill.acciones.length === 0 && (
          <p className="text-xs text-muted-foreground">{t('constructores.rendicion.sinAcciones')}</p>
        )}
        {prefill.acciones.map((accion, i) => (
          <p key={i} className="text-xs text-muted-foreground">
            {accion.descripcion} — {accion.estado}
            {accion.responsable ? ` · ${accion.responsable}` : ''}
          </p>
        ))}
      </div>
      <div className="space-y-1">
        <PrefillHeading>{t('constructores.rendicion.indicadores')}</PrefillHeading>
        {prefill.indicadores.length === 0 && (
          <p className="text-xs text-muted-foreground">{t('constructores.rendicion.sinIndicadores')}</p>
        )}
        {prefill.indicadores.map((indicador, i) => (
          <p key={i} className="text-xs text-muted-foreground">
            {indicador.nombre} · {indicador.periodo}: <span className="font-mono">{indicador.valor}</span>
          </p>
        ))}
      </div>
    </div>
  )
}

function EppPrefillSection({
  data,
  epp,
  incluidos,
  onEpp,
  onIncluido,
}: {
  data: Constructor
  epp: Record<string, string>
  incluidos: Record<string, boolean>
  onEpp: (id: string, valor: string) => void
  onIncluido: (id: string, valor: boolean) => void
}) {
  const {t} = useTranslation()
  return (
    <div className="space-y-3" data-testid="epp-prefill">
      <PrefillHeading>
        {t('constructores.epp.roster')}
        <HelpIcon label={t('constructores.epp.ayudaLabel')} testId="epp-ayuda-tip">
          {t('constructores.epp.ayuda')}
        </HelpIcon>
      </PrefillHeading>
      {data.epp!.length === 0 && <p className="text-xs text-muted-foreground">{t('constructores.epp.sinRoster')}</p>}
      {data.epp!.map((persona) => (
        <div key={persona.employee_id} className="space-y-1 border-b border-border pb-2 last:border-b-0">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={incluidos[persona.employee_id] ?? false}
              onCheckedChange={(checked) => onIncluido(persona.employee_id, checked === true)}
              data-testid={`epp-incluir-${persona.employee_id}`}
            />
            <p className="text-sm">
              {persona.nombre}
              {persona.cargos.length > 0 && (
                <span className="text-xs text-muted-foreground"> · {persona.cargos.join(', ')}</span>
              )}
            </p>
          </div>
          {persona.peligros.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {t('constructores.epp.peligros', {peligros: persona.peligros.join(', ')})}
            </p>
          )}
          <Input
            maxLength={tope('NOM')}
            value={epp[persona.employee_id] ?? ''}
            onChange={(e) => onEpp(persona.employee_id, e.target.value)}
            placeholder={t('constructores.epp.placeholder')}
            disabled={!incluidos[persona.employee_id]}
            data-testid={`epp-input-${persona.employee_id}`}
          />
        </div>
      ))}
    </div>
  )
}

function EstilosVidaPrefillSection({
  data,
  actividades,
  onCampo,
}: {
  data: Constructor
  actividades: Record<string, {fecha_programada: string; responsable: string; observaciones: string}>
  onCampo: (titulo: string, campo: 'fecha_programada' | 'responsable' | 'observaciones', valor: string) => void
}) {
  const {t} = useTranslation()
  const prefill = data.estilos_vida!
  return (
    <div className="space-y-3" data-testid="estilos-vida-prefill">
      <div className="rounded-lg bg-secondary/50 p-3 text-sm">
        {t(
          prefill.diagnostico_disponible
            ? 'constructores.estilosVida.diagnosticoDisponible'
            : 'constructores.estilosVida.diagnosticoNoDisponible'
        )}
      </div>
      <PrefillHeading>{t('constructores.estilosVida.actividades')}</PrefillHeading>
      {prefill.actividades_obligatorias.map((titulo) => (
        <div key={titulo} className="space-y-2 border-b border-border pb-2 last:border-b-0">
          <p className="text-sm font-medium">{titulo}</p>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label htmlFor={`estilos-vida-fecha-${titulo}`}>{t('constructores.estilosVida.fechaProgramada')}</Label>
              <Input
                id={`estilos-vida-fecha-${titulo}`}
                type="date"
                value={actividades[titulo]?.fecha_programada ?? ''}
                onChange={(e) => onCampo(titulo, 'fecha_programada', e.target.value)}
                data-testid={`estilos-vida-fecha-${titulo}`}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor={`estilos-vida-responsable-${titulo}`}>{t('constructores.estilosVida.responsable')}</Label>
              <Input
                maxLength={tope('NOTA')}
                id={`estilos-vida-responsable-${titulo}`}
                value={actividades[titulo]?.responsable ?? ''}
                onChange={(e) => onCampo(titulo, 'responsable', e.target.value)}
                data-testid={`estilos-vida-responsable-${titulo}`}
              />
            </div>
          </div>
          <Textarea
            maxLength={tope('NOTA')}
            value={actividades[titulo]?.observaciones ?? ''}
            onChange={(e) => onCampo(titulo, 'observaciones', e.target.value)}
            placeholder={t('constructores.estilosVida.observaciones')}
            data-testid={`estilos-vida-observaciones-${titulo}`}
          />
        </div>
      ))}
    </div>
  )
}
